import api from './api';

const CHUNK_SIZE = 512 * 1024; // 512KB/chunk (~683KB em base64, ainda sob o cap ~1MB do proxy).
// Metade dos round-trips vs 256KB: vídeo de iPhone (~100MB) vira ~200 chunks em vez de ~400.
const CHUNK_TIMEOUT = 120000; // 120s/chunk (era 60s): link móvel fraco não estoura tão fácil.
const CHUNK_RETRIES = 1;      // 1 retry por chunk: 1 falha de rede não mata o upload inteiro.

/**
 * Upload a file using chunked upload strategy.
 * Splits the file into small base64 chunks and sends each one individually.
 * This bypasses proxy body size limits (e.g., Kubernetes ingress 1MB limit).
 *
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback (0-100)
 * @param {string} [validate] - Optional server-side type check on complete:
 *        'anexo' (whitelist de anexos do chat) ou 'imagem' (só imagens, p/ logo).
 * @param {boolean} [provisorio] - true quando o arquivo é de um formulário que ainda não virou
 *        documento (o modal de nova OS). Habilita o descarte e a varredura de órfãos.
 * @returns {Promise<{url: string, filename: string, size: number}>}
 */
export async function uploadFileChunked(file, onProgress, validate, provisorio = false) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

  // Step 1: Initialize upload session
  const initRes = await api.post('/uploads/chunked/init', {
    filename: file.name,
    total_chunks: totalChunks,
    file_size: file.size,
    content_type: file.type,
    ...(validate ? { validate_kind: validate } : {}),
  });
  const { upload_id } = initRes.data;

  // Step 2: Ler o arquivo SOB DEMANDA por chunk (file.slice), sem materializar o arquivo
  // inteiro em memória — vídeos de centenas de MB não estouram a aba mobile.
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, file.size);
    const blobSlice = file.slice(start, end);
    const chunkBytes = new Uint8Array(await blobSlice.arrayBuffer());

    // Convert to base64
    let binary = '';
    const len = chunkBytes.byteLength;
    for (let j = 0; j < len; j++) {
      binary += String.fromCharCode(chunkBytes[j]);
    }
    const base64Data = btoa(binary);

    // Reenvia o MESMO chunk (idempotente por chunk_index) até CHUNK_RETRIES antes de desistir.
    for (let tentativa = 0; ; tentativa++) {
      try {
        await api.post('/uploads/chunked/part', {
          upload_id,
          chunk_index: i,
          data: base64Data,
        }, { timeout: CHUNK_TIMEOUT });
        break;
      } catch (err) {
        if (tentativa >= CHUNK_RETRIES) throw err;
        await new Promise((r) => setTimeout(r, 1000));
      }
    }

    if (onProgress) {
      onProgress(Math.round(((i + 1) / totalChunks) * 100));
    }
  }

  // Step 3: Complete the upload
  const completeRes = await api.post('/uploads/chunked/complete', {
    upload_id,
    // PROVISORIO = "este arquivo e de um formulario que ainda nao virou documento". O servidor
    // marca; quem grava o anexo num documento desmarca; e so o que continua marcado pode ser
    // descartado. Ver o bloco UPLOAD PROVISORIO em backend/routes/uploads.py.
    ...(provisorio ? { provisorio: true } : {}),
  });

  return completeRes.data;
}
